import React, { useEffect, useRef } from 'react';
import { Box, Text } from '@wix/design-system';
import { useConfigContext } from '../../config-context';
import { useMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import { SolutionGridWrapper as AppGrid } from '../solution-grid-wrapper/solution-grid-wrapper';
import { CategoryPageHeader } from '../category-page-header';
import { SubCategorySection } from '../sub-category-section';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import { CenteredLoader } from '../centered-loader';
import { Order } from '@wix/ambassador-marketplace/types';
import { config } from '../../config';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';
import { ErrorPageContainer } from '../error-page/error-page-container';
import { ErrorPageType } from '../../enums/marketplace-enums';
import { biLogger } from '../../bi';
import { appMarketPageView } from '@wix/bi-logger-market/v2';
import {
  getPathForBi,
  getReferralInfo,
  getWidgetReferral,
} from '../../common/bi-events';
import type { Category, SubCategory } from './category-page.utils';
import {
  AppsCount,
  getCategories,
  getWebSolutionsByTags,
  isValidCategory,
} from './category-page.utils';

async function fetchCategoryData(
  slug: string,
  languageCode: string,
  countryCode: string = 'US',
  rpcClient?: AppMarketRpcClient,
): Promise<Category> {
  const [getWebSolutionsByTagsResponse, getCategoriesResponse] =
    await Promise.all([
      getWebSolutionsByTags(
        countryCode,
        languageCode,
        200,
        Order.GEO,
        slug,
        rpcClient,
      ),
      getCategories(languageCode, rpcClient),
    ]);

  const name =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.name;
  const description =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.description;
  const imageUrl =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.assetsMap
      ?.collectionImage?.assets?.[0]?.url;
  const subCategorySlugs = getCategoriesResponse.categoryToSubCategory
    ?.find(
      (categoryToSubCategory) => categoryToSubCategory.category?.slug === slug,
    )
    ?.subCategories?.map((subCategory) => subCategory.slug);

  const subCategories = await Promise.all(
    subCategorySlugs.map((subCategorySlug) =>
      fetchSubCategoryData(
        subCategorySlug,
        languageCode,
        countryCode,
        rpcClient,
      ),
    ),
  );

  const apps = getWebSolutionsByTagsResponse.webSolutions?.map((app) => ({
    appIndex: app.baseInfo.id,
    ...app.baseInfo,
  }));

  return { slug, name, description, imageUrl, subCategories, apps };
}

async function fetchSubCategoryData(
  slug: string,
  languageCode: string,
  countryCode: string = 'US',
  rpcClient?: AppMarketRpcClient,
): Promise<SubCategory> {
  const getWebSolutionsByTagsResponse = await getWebSolutionsByTags(
    countryCode,
    languageCode,
    200,
    Order.GEO,
    slug,
    rpcClient,
  );

  const name =
    getWebSolutionsByTagsResponse.tagsAndSubCategories?.[0]?.tag?.name;

  const apps = getWebSolutionsByTagsResponse.webSolutions?.map((app) => ({
    appIndex: app.baseInfo?.id ?? '',
    ...app.baseInfo,
  }));

  return { slug, name, apps };
}

export type CategoryPageProps = {
  appsGrouping?: 'none' | 'sub-categories';
};

export function CategoryPage({ appsGrouping = 'none' }: CategoryPageProps) {
  const { route, prevRoute, metaSiteId } = useMarketplaceStore();
  const { locale: languageCode, countryCode, rpcClient } = useConfigContext();
  const isFirstRenderRef = useRef<boolean>(true);
  const refetchRef = useRef<any>(() => {});

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      refetchRef.current(() =>
        fetchCategoryData(route.slug, languageCode, countryCode),
      );
    }

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
  }, [route.slug, languageCode]);

  useEffect(() => {
    biLogger.report(
      appMarketPageView({
        app_id: null,
        market: config.originForBI,
        navigation_source: route.referral,
        tag_name: route.slug.replace(/-/g, '_'),
        tag_type: 'category',
        msid: metaSiteId,
        referral_info: getPathForBi(prevRoute?.path),
        search_term: prevRoute.query,
        widget_referral: getWidgetReferral(),
        utm_id: route.utm_id,
        referral_tag_name: prevRoute?.slug?.replace(/-/g, '_'),
        referral_sub_tag_name: prevRoute?.subCategories?.join(','),
        is_external: route?.referral && route?.referral !== 'manage_apps',
      }),
    );
  }, [route.slug]);

  return (
    <WithFetcher
      initialFetch={() =>
        fetchCategoryData(route.slug, languageCode, countryCode, rpcClient)
      }
      fetchMemoizationId={`category-page-${route.slug}-${languageCode}`}
      loader={<CenteredLoader />}
    >
      {({ fetchData: category, reFetch }) => {
        refetchRef.current = reFetch;

        if (!isValidCategory(category)) {
          return (
            <ErrorPageContainer
              slug={route.slug}
              errorPageType={ErrorPageType.NOT_FOUND}
            />
          );
        }

        return (
          <Box
            align="center"
            paddingTop="24px"
            paddingBottom="24px"
            paddingLeft="48px"
            paddingRight="48px"
            direction="vertical"
          >
            <Box direction="vertical" minWidth="864px" maxWidth="1248px">
              <CategoryPageHeader
                title={category.name}
                subtitle={category.description}
                imageUrl={category.imageUrl}
              />

              {appsGrouping === 'none' ? (
                <Box direction="vertical" paddingTop="36px">
                  <Text weight="thin">
                    <AppsCount count={category.apps.length} />
                  </Text>
                  <AppGrid solutions={category.apps} />
                </Box>
              ) : null}

              {appsGrouping === 'sub-categories'
                ? category.subCategories.map((subCategory) => (
                    <Box
                      key={subCategory.slug}
                      direction="vertical"
                      paddingTop="36px"
                    >
                      <SubCategorySection
                        slug={subCategory.slug}
                        title={subCategory.name}
                        apps={subCategory.apps}
                      />
                    </Box>
                  ))
                : null}
            </Box>
          </Box>
        );
      }}
    </WithFetcher>
  );
}
